import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "../../components/app/AppShell";
import { FullPageLoading } from "../../components/Loading";
import {
  getChapter,
  listChapters,
  listProfiles,
  type ChapterDetail,
  type ChapterSummary,
} from "../../lib/api";
import { chapterNumber, chapterTitleWithoutNumber, toRoman } from "../../lib/format";

const ROMAN = [
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
  "XI",
  "XII",
  "XIII",
];

export default function AppChapter() {
  const { n } = useParams<{ n: string }>();
  const num = Math.max(1, parseInt(n || "1", 10) || 1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chapter, setChapter] = useState<ChapterDetail | null>(null);
  const [allChapters, setAllChapters] = useState<ChapterSummary[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await listProfiles();
        const p = (r.profiles || [])[0] || null;
        if (!p) {
          setLoading(false);
          return;
        }
        const ch = await listChapters(p.phone);
        const sorted = [...(ch.chapters || [])].sort(
          (a, b) => chapterNumber(a.title) - chapterNumber(b.title)
        );
        setAllChapters(sorted);
        const match = sorted[num - 1];
        if (!match) {
          setError("Dieses Kapitel existiert noch nicht.");
          setLoading(false);
          return;
        }
        const detail = await getChapter(p.phone, match.title);
        setChapter(detail);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Fehler beim Laden.");
      } finally {
        setLoading(false);
      }
    })();
  }, [num]);

  if (loading) return <FullPageLoading />;

  const roman = ROMAN[num - 1] || String(num);
  const titleText = (chapter?.title || "").replace(/^\d+\s*[·.\-:]?\s*/, "");

  // Split content into paragraphs for proper typesetting.
  const paragraphs = (chapter?.content || "")
    .split(/\n\s*\n+/g)
    .map((p) => p.trim())
    .filter(Boolean);
  const isEmpty = paragraphs.length === 0;

  // Indexes (1-based) of chapters that already have content. Used for
  // smart prev/next + the "Andere Kapitel mit Inhalt"-list shown when
  // the current chapter is still empty.
  const indexesWithContent = allChapters
    .map((c, idx) => ({ c, n: idx + 1 }))
    .filter(({ c }) => (c.content_length || 0) > 0);
  const prevWithContent = [...indexesWithContent]
    .reverse()
    .find(({ n }) => n < num);
  const nextWithContent = indexesWithContent.find(({ n }) => n > num);

  return (
    <AppShell
      eyebrow={`Kapitel ${roman}`}
      title={
        chapter ? (
          <em>{titleText || "Kapitel"}</em>
        ) : (
          <em>Kapitel {roman}</em>
        )
      }
    >
      <div style={{ padding: "0 18px 90px" }}>
        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(107,32,24,0.08)",
              border: "1px solid rgba(107,32,24,0.25)",
              borderLeft: "3px solid var(--oxblood)",
              borderRadius: 4,
              padding: "10px 14px",
              color: "var(--oxblood-d)",
              fontFamily: "var(--f-body)",
              fontSize: 14,
              marginBottom: 18,
            }}
          >
            {error}
          </div>
        )}

        {chapter && (
          <article
            style={{
              background: "var(--paper-50)",
              borderRadius: 3,
              padding: "32px 24px 28px",
              border: "1px solid rgba(43,26,15,0.10)",
              boxShadow: "0 16px 28px -10px rgba(43,26,15,0.18)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 22 }}>
              <div className="t-caps" style={{ color: "var(--ink-faint)" }}>
                Kapitel
              </div>
              <div
                style={{
                  fontFamily: "var(--f-display)",
                  fontStyle: "italic",
                  fontSize: 56,
                  lineHeight: 1,
                  color: "var(--oxblood)",
                  marginTop: 2,
                }}
              >
                {roman}
              </div>
              <div
                className="fleuron"
                style={{ margin: "10px auto 12px", color: "var(--ochre)", maxWidth: 160 }}
                aria-hidden
              >
                ❦
              </div>
              <h2
                style={{
                  fontFamily: "var(--f-display)",
                  fontWeight: 500,
                  fontSize: 24,
                  lineHeight: 1.1,
                  margin: 0,
                  color: "var(--ink)",
                }}
              >
                {titleText || "Kapitel"}
              </h2>
            </div>

            {paragraphs.map((p, i) => {
              const isFirst = i === 0;
              const showMarginNote = i === 1 && chapter.analyses?.[0];
              const showInlinePhoto = i === 2;
              return (
                <div key={i}>
                  <p
                    className={isFirst ? "dropcap" : undefined}
                    style={{
                      fontFamily: "var(--f-display)",
                      fontSize: 17,
                      lineHeight: 1.6,
                      color: "var(--ink)",
                      margin: "0 0 16px",
                      textAlign: "justify",
                    }}
                  >
                    {p}
                  </p>

                  {showMarginNote && (
                    <div
                      style={{
                        margin: "0 -8px 16px",
                        padding: "12px 16px",
                        background: "rgba(168,122,58,0.10)",
                        borderLeft: "3px solid var(--ochre)",
                        fontFamily: "var(--f-script)",
                        fontSize: 20,
                        lineHeight: 1.2,
                        color: "var(--oxblood)",
                        transform: "rotate(-0.3deg)",
                      }}
                    >
                      „
                      {(chapter.analyses[0] || "").slice(0, 120)}
                      "
                    </div>
                  )}

                  {showInlinePhoto && (
                    <div style={{ margin: "20px -4px 18px" }}>
                      <img
                        src="/images/hildegard-1957.jpg"
                        alt="Hildegard, 1957"
                        className="photo-tone"
                        style={{
                          display: "block",
                          width: "100%",
                          height: 180,
                          objectFit: "cover",
                          borderRadius: 2,
                          border: "1px solid rgba(43,26,15,0.14)",
                        }}
                      />
                      <div
                        style={{
                          fontFamily: "var(--f-script)",
                          fontSize: 16,
                          color: "var(--ink-faint)",
                          textAlign: "center",
                          marginTop: 4,
                          transform: "rotate(-1.5deg)",
                        }}
                      >
                        Aus Hildegards Album
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {isEmpty && (
              <>
                <p
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 15,
                    color: "var(--ink-faint)",
                    textAlign: "center",
                    margin: "20px 0 24px",
                  }}
                >
                  Dieses Kapitel wartet noch auf Inhalt — der nächste Anruf
                  bringt neue Worte.
                </p>

                {indexesWithContent.length > 0 && (
                  <div
                    style={{
                      borderTop: "1px dashed rgba(43,26,15,0.18)",
                      paddingTop: 18,
                      marginTop: 18,
                    }}
                  >
                    <div
                      className="t-caps"
                      style={{ marginBottom: 10, color: "var(--ink-faint)" }}
                    >
                      Schon geschrieben
                    </div>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      {indexesWithContent.map(({ c, n: chN }) => (
                        <li key={c.title}>
                          <Link
                            to={`/app/chapter/${chN}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "8px 4px",
                              textDecoration: "none",
                              color: "var(--ink)",
                              borderBottom: "1px solid rgba(43,26,15,0.06)",
                            }}
                          >
                            <span
                              style={{
                                fontFamily: "var(--f-display)",
                                fontStyle: "italic",
                                fontSize: 15,
                                color: "var(--oxblood)",
                                minWidth: 28,
                                textAlign: "center",
                              }}
                            >
                              {toRoman(chN)}
                            </span>
                            <span
                              style={{
                                flex: 1,
                                fontFamily: "var(--f-display)",
                                fontWeight: 500,
                                fontSize: 14.5,
                                color: "var(--ink)",
                              }}
                            >
                              {chapterTitleWithoutNumber(c.title) || c.title}
                            </span>
                            <span
                              style={{
                                fontFamily: "var(--f-ui)",
                                fontSize: 11,
                                color: "var(--ink-faint)",
                              }}
                            >
                              {c.saturation_score || 0} %
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </article>
        )}

        {chapter && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 18,
              padding: "0 4px",
              gap: 12,
            }}
          >
            {prevWithContent ? (
              <Link
                to={`/app/chapter/${prevWithContent.n}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "var(--f-display)",
                  fontStyle: "italic",
                  fontSize: 13,
                  color: "var(--ink-soft)",
                  textDecoration: "none",
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                Kapitel {toRoman(prevWithContent.n)}
              </Link>
            ) : (
              <span />
            )}
            <Link
              to="/app/book"
              style={{
                fontFamily: "var(--f-ui)",
                fontSize: 11,
                color: "var(--ink-faint)",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              Alle Kapitel
            </Link>
            {nextWithContent ? (
              <Link
                to={`/app/chapter/${nextWithContent.n}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "var(--f-display)",
                  fontStyle: "italic",
                  fontSize: 13,
                  color: "var(--oxblood)",
                  textDecoration: "none",
                }}
              >
                Kapitel {toRoman(nextWithContent.n)}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </Link>
            ) : (
              <span />
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
